import { Percent } from 'lucide-react';
import { useState } from 'react';

import { NumberStepper } from '@/components/sales/wizard/NumberStepper';
import { Button } from '@/components/ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';
import { inr, type ProductDiscount } from '@/lib/sales-metrics';
import cn from '@/utils/cn';
import { normalizeDiscountValue, resolveLineDiscountAmount } from '@/utils/productTermPricing';

interface ProductDiscountControlProps {
  discount: ProductDiscount | undefined;
  grossLineTotal: number;
  onChange: (discount: ProductDiscount | null) => void;
  ariaLabel: string;
}

// Per-product discount editor (ADR-019): a % or fixed-amount discount taken off
// this one product's gross line total. Clearing the value removes the discount.
export function ProductDiscountControl({
  discount,
  grossLineTotal,
  onChange,
  ariaLabel,
}: ProductDiscountControlProps) {
  const [mode, setMode] = useState<'amount' | 'percent'>(discount?.mode ?? 'percent');
  const value = discount?.value ?? 0;
  const applied = resolveLineDiscountAmount(mode, value, grossLineTotal);

  const update = (nextMode: 'amount' | 'percent', nextValue: number) => {
    // Normalize to the stored precision (percent 2dp, amount whole rupees) so the
    // in-memory value the live quote uses matches what the server persists (ADR-036).
    const normalized = normalizeDiscountValue(nextMode, nextValue);
    onChange(normalized > 0 ? { mode: nextMode, value: normalized } : null);
  };

  const selectMode = (nextMode: 'amount' | 'percent') => {
    setMode(nextMode);
    update(nextMode, value);
  };

  return (
    <Popover>
      <PopoverTrigger asChild>
        <button
          type="button"
          aria-label={ariaLabel}
          className={cn(
            'mt-1 inline-flex items-center gap-1 rounded border px-1.5 py-0.5 text-[10px] font-semibold transition-colors',
            applied > 0
              ? 'border-emerald-300 bg-emerald-50 text-emerald-700'
              : 'border-border text-muted-foreground hover:bg-muted hover:text-foreground',
          )}
        >
          <Percent className="h-3 w-3" />
          {applied > 0 ? `-${inr(applied)}` : 'Discount'}
        </button>
      </PopoverTrigger>
      <PopoverContent align="end" className="w-56 p-3">
        <div className="flex flex-col gap-3">
          <span className="text-xs font-semibold text-foreground">Product discount</span>
          <div className="inline-flex w-fit items-center gap-0.5 self-start rounded border border-border bg-muted/40 p-0.5 text-[10px]">
            <button
              type="button"
              className={cn(
                'rounded px-2 py-0.5 font-semibold transition-all',
                mode === 'amount'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
              onClick={() => selectMode('amount')}
            >
              ₹ Value
            </button>
            <button
              type="button"
              className={cn(
                'rounded px-2 py-0.5 font-semibold transition-all',
                mode === 'percent'
                  ? 'bg-background text-foreground shadow-sm'
                  : 'text-muted-foreground hover:text-foreground',
              )}
              onClick={() => selectMode('percent')}
            >
              % Off
            </button>
          </div>
          <div className="flex items-center gap-2">
            <NumberStepper
              value={value}
              onChange={(v) => update(mode, v)}
              max={mode === 'percent' ? 100 : undefined}
              step={mode === 'percent' ? 1 : 100}
              hideZero
              inputWidthClassName="w-14"
              placeholder="0"
              ariaLabel="Discount value"
            />
            <span className="text-xs text-muted-foreground">{mode === 'percent' ? '%' : '₹'}</span>
          </div>
          {applied > 0 && (
            <div className="flex items-center justify-between rounded-md bg-emerald-50 px-2.5 py-1.5 text-xs text-emerald-800">
              <span>
                Applied: <strong>-{inr(applied)}</strong>
              </span>
              <Button
                variant="ghost"
                size="sm"
                className="h-6 px-2 text-[11px] text-muted-foreground"
                onClick={() => onChange(null)}
              >
                Clear
              </Button>
            </div>
          )}
        </div>
      </PopoverContent>
    </Popover>
  );
}
